import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { type ContentSelectionMode } from '@databases/redux/cellSelections/selectors';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { ActionComponent } from './ActionComponent';
import { type UseSelectEntriesActionsReturnTypes } from './actions/build-select-entries-actions';
import { type UseSelectFieldActionsReturnTypes } from './actions/build-select-fields-actions';
import { type UseSelectSelectValuesActionsReturnTypes } from './actions/build-select-values-actions';
import {
  SelectEntriesActions,
  type SelectEntriesActionsProps,
} from './entries/SelectEntriesActions';
import {
  SelectFieldsActions,
  type SelectFieldsActionsProps,
} from './fields/SelectFieldsActions';
import { messages } from './messages';
import {
  SelectValuesActions,
  type SelectValuesActionsProps,
} from './values/SelectValuesActions';

const contentContainerStyles = xcss({
  background: token('color.background.neutral.bold', '#292A2E'),
  borderRadius: token('space.075', '6px'),
  border: `1px solid ${token('color.border')}`,
  boxShadow: 'elevation.shadow.overflow',
  display: 'flex',
});

const selectedEntriesStyles = xcss({
  paddingRight: 'space.200',
});

const verticalSeparatorStyles = xcss({
  width: '1px',
  height: '20px',
  marginRight: 'space.200',
  background: token('color.border.inverse'),
});

const closeIconContainerStyles = xcss({
  cursor: 'pointer',
  paddingLeft: 'space.150',
});

const actionContainerStyles = xcss({ alignItems: 'center', gap: 'space.0' });

type BaseActionBarContentProps = {
  canEditContent: boolean;
  contentId: string;
  count: number;
  fieldsWithSelectionsCount: number;
  handleDeleteFields: () => void;
  handleOnCloseActionBar: () => void;
  isDatabaseLocked: boolean;
  isDeleteColumnsDisabled: boolean;
  selectedEntriesCount: number;
  shouldShowClearCells: boolean;
} & Pick<
  SelectValuesActionsProps,
  'initialValue' | 'isDependencyFieldSelected' | 'selectedValues'
> &
  Pick<
    SelectEntriesActionsProps,
    'isEntryPanelActive' | 'hasSortedFields' | 'canMoveUp' | 'canMoveDown'
  > &
  Pick<SelectFieldsActionsProps, 'canMoveLeft' | 'canMoveRight'>;

export type ActionBarContentProps =
  | (BaseActionBarContentProps & {
      actions: UseSelectEntriesActionsReturnTypes;
      selectionMode: 'row';
    })
  | (BaseActionBarContentProps & {
      actions: UseSelectFieldActionsReturnTypes;
      selectionMode: 'column';
    })
  | (BaseActionBarContentProps & {
      actions: UseSelectSelectValuesActionsReturnTypes;
      selectionMode: 'cell';
    })
  | (BaseActionBarContentProps & {
      actions:
        | UseSelectEntriesActionsReturnTypes
        | UseSelectFieldActionsReturnTypes
        | UseSelectSelectValuesActionsReturnTypes;
      selectionMode: ContentSelectionMode;
    });

export const ActionBarContent = ({
  actions,
  canEditContent,
  canMoveUp,
  canMoveDown,
  canMoveLeft,
  canMoveRight,
  contentId,
  count,
  fieldsWithSelectionsCount,
  handleDeleteFields,
  handleOnCloseActionBar,
  hasSortedFields,
  initialValue,
  isDatabaseLocked,
  isDeleteColumnsDisabled,
  isDependencyFieldSelected,
  isEntryPanelActive,
  selectedEntriesCount,
  selectedValues,
  selectionMode,
  shouldShowClearCells,
}: ActionBarContentProps) => {
  const { formatMessage } = useIntl();
  const isReadOnly = !canEditContent;
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const renderActions = () => {
    if (selectionMode === 'column') {
      return (
        <SelectFieldsActions
          actions={actions as UseSelectFieldActionsReturnTypes}
          canMoveLeft={canMoveLeft}
          canMoveRight={canMoveRight}
          handleDeleteFields={handleDeleteFields}
          isDeleteDisabled={isDeleteColumnsDisabled}
          shouldShowClearCells={shouldShowClearCells}
        />
      );
    }

    if (selectionMode === 'cell') {
      return (
        <SelectValuesActions
          actions={actions as UseSelectSelectValuesActionsReturnTypes}
          contentId={contentId}
          fieldsWithSelectionsCount={fieldsWithSelectionsCount}
          handleDeleteFields={handleDeleteFields}
          initialValue={initialValue}
          isDeleteColumnsDisabled={isDeleteColumnsDisabled || isDatabaseLocked}
          isDependencyFieldSelected={isDependencyFieldSelected}
          selectedEntriesCount={selectedEntriesCount}
          selectedValues={selectedValues}
          shouldShowClearCells={shouldShowClearCells}
        />
      );
    }

    return (
      <SelectEntriesActions
        actions={actions as UseSelectEntriesActionsReturnTypes}
        canEditContent={canEditContent}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        hasSortedFields={hasSortedFields}
        isEntryPanelActive={isEntryPanelActive}
        selectedEntriesCount={selectedEntriesCount}
      />
    );
  };

  return (
    <Box paddingBlock="space.075" paddingInline="space.300" xcss={contentContainerStyles}>
      <Inline space="space.200" xcss={actionContainerStyles}>
        {!isReadOnly && (
          <>
            <Box xcss={selectedEntriesStyles}>
              <Inline space="space.150" alignBlock="center">
                <Text color="color.text.inverse" weight="medium">
                  {formatMessage(messages['action-bar.selected-count'], {
                    count,
                  })}
                </Text>
                {isDBAiFirstCreationEnabled && (
                  <ActionComponent
                    type="ask-rovo"
                    message={formatMessage(messages['action-bar.ask-rovo'])}
                    iconBefore={<AskRovoIcon noPadding />}
                    onClick={actions.handleAskRovo}
                  />
                )}
              </Inline>
            </Box>
            <Box xcss={verticalSeparatorStyles} />
          </>
        )}
        {renderActions()}
        <Box
          testId="close-action-bar-button"
          xcss={closeIconContainerStyles}
          onClick={handleOnCloseActionBar}
        >
          <CrossIcon
            color={token('color.text.inverse')}
            label={formatMessage(messages['action-bar.close-icon-label'])}
          />
        </Box>
      </Inline>
    </Box>
  );
};
